import React, { useEffect, useState, useCallback } from 'react';
import { payrollAPI } from '../../lib/api';
import { toast } from 'sonner';
import { extractErrorMessage } from '../../lib/errors';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, Link2, AlertTriangle, Check } from 'lucide-react';
import { formatDate } from '../../lib/utils';

// Hours type → earnings code map (item 9). Every WinTeam hours type must resolve
// to an earnings code so gross-pay parity knows how to treat its hours.
export default function HoursTypeMapCard({ refreshKey }) {
  const [map, setMap] = useState([]);
  const [codes, setCodes] = useState([]);
  const [unmapped, setUnmapped] = useState([]);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState('');

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [m, c, u] = await Promise.all([
        payrollAPI.hoursTypeMap(), payrollAPI.earningsCodes(), payrollAPI.hoursTypeMapUnmapped(),
      ]);
      setMap(m.data.map || []);
      setCodes(c.data.earnings_codes || []);
      setUnmapped(u.data.codes || []);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Could not load the hours-type map'));
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const setMapping = async (row, code) => {
    const key = row.hours_type_code || row.hours_type_name;
    setSaving(key);
    try {
      await payrollAPI.setHoursTypeMap(row.hours_type_name, code, row.hours_type_code || undefined);
      toast.success(`Mapped ${row.hours_type_name || row.hours_type_code} → ${code}`);
      load();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Could not save the mapping'));
    } finally { setSaving(''); }
  };

  const codeOptions = codes.map((c) => c.code);

  const rows = [
    ...map.map((m) => ({ ...m, _key: `m-${m.hours_type_code || ''}-${m.hours_type_name || ''}` })),
    ...unmapped.map((u) => ({
      hours_type_code: u.code, hours_type_name: u.description || '',
      earnings_code: '', source: 'unmapped', _key: `u-${u.code}-${u.description}`,
    })),
  ];

  return (
    <Card data-testid="payroll-hours-type-map">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Link2 className="w-5 h-5" /> Hours type → earnings code map</CardTitle>
        <CardDescription>
          How each WinTeam hours type is treated in the gross-pay parallel test, matched on the
          SAR hours-type code and/or description. Overtime, PTO, salary and the like each resolve
          to an earnings code. Changes are effective-dated and audited.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {unmapped.length > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="hours-map-unmapped">
            <div className="flex items-center gap-2 font-medium"><AlertTriangle className="w-4 h-4" /> {unmapped.length} hours type code{unmapped.length === 1 ? '' : 's'} in the SAR not yet mapped</div>
            <p className="mt-1 text-xs">{unmapped.map((u) => `${u.code || '—'}${u.description ? ` (${u.description})` : ''}`).join(', ')}</p>
          </div>
        )}
        {busy ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <div className="overflow-auto max-h-96 border rounded">
            <Table>
              <TableHeader><TableRow>
                <TableHead className="w-20">Code</TableHead>
                <TableHead>Hours type (description)</TableHead>
                <TableHead>Earnings code</TableHead>
                <TableHead className="w-28">Counts toward 40</TableHead>
                <TableHead className="w-36">Already paid at a premium</TableHead>
                <TableHead className="w-28">Effective</TableHead>
                <TableHead className="w-24">Source</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r._key} data-testid={`hours-map-row-${r.hours_type_code || r.hours_type_name}`}>
                    <TableCell className="text-sm font-mono" data-testid={`hours-map-code-${r.hours_type_code || r.hours_type_name}`}>{r.hours_type_code || <span className="text-slate-300">—</span>}</TableCell>
                    <TableCell className="text-sm font-medium">{r.hours_type_name || <span className="text-slate-300">—</span>}</TableCell>
                    <TableCell>
                      <Select value={r.earnings_code || undefined} onValueChange={(v) => setMapping(r, v)}>
                        <SelectTrigger className="w-56" data-testid={`hours-map-select-${r.hours_type_code || r.hours_type_name}`}>
                          <SelectValue placeholder="Choose…" />
                        </SelectTrigger>
                        <SelectContent>
                          {codeOptions.map((code) => (
                            <SelectItem key={code} value={code} data-testid={`hours-map-opt-${r.hours_type_code || r.hours_type_name}-${code}`}>{code}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {saving === (r.hours_type_code || r.hours_type_name) && <Loader2 className="inline w-3 h-3 ml-2 animate-spin text-slate-400" />}
                    </TableCell>
                    <TableCell className="text-sm" data-testid={`hours-map-ot-${r.hours_type_code || r.hours_type_name}`}>
                      {r.earnings_code
                        ? (r.counts_toward_40 ? <span className="inline-flex items-center gap-1 text-emerald-700"><Check className="w-3.5 h-3.5" /> Yes</span> : <span className="text-slate-500">No</span>)
                        : <span className="text-slate-300">—</span>}
                    </TableCell>
                    <TableCell className="text-sm" data-testid={`hours-map-premium-${r.hours_type_code || r.hours_type_name}`}>
                      {r.earnings_code
                        ? (r.already_paid_at_premium ? <span className="inline-flex items-center gap-1 text-violet-700"><Check className="w-3.5 h-3.5" /> Yes</span> : <span className="text-slate-500">No</span>)
                        : <span className="text-slate-300">—</span>}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500" data-testid={`hours-map-eff-${r.hours_type_code || r.hours_type_name}`}>
                      {r.effective_date ? formatDate(r.effective_date) : '—'}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {r.source === 'unmapped'
                        ? <Badge variant="destructive">unmapped</Badge>
                        : r.source === 'seed' ? <Badge variant="outline">seed</Badge> : <Badge variant="secondary">edited</Badge>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
